import { AfterViewInit, ChangeDetectionStrategy, Component, ContentChild, ElementRef, forwardRef, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, map, startWith, tap } from 'rxjs/operators';
import { FilterByPipe } from 'src/app/shared/pipes';
import { OptionTemplateDirective } from './directives/option-template.directive';

@Component({
    selector: 'app-multi-selector-form-field',
    templateUrl: './multi-selector-form-field.component.html',
    styleUrls: ['./multi-selector-form-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiSelectorFormFieldComponent),
            multi: true,
        },
        FilterByPipe
    ],
    standalone: false
})
export class MultiSelectorFormFieldComponent<T, R> implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {
    @Input() set items(value: T[]) {
        this.innerItemsSubject.next(value);
    }
    get items(): T[] {
        return this.innerItemsSubject.getValue();
    }
    @Input() bindLabel: string;
    @Input() bindValue: string;
    @Input() label: string;
    @Input() placeholder: string;
    @Input() isLoading: boolean;
    @Input() showSearchInput: boolean = true;
    @Input() showChipsList: boolean = false;
    @Input() listElementTooltipIfTruncated: boolean = true;

    @ContentChild('preOption') preOptionTemplate: TemplateRef<any>;
    @ContentChild('preOptionEmpty') preOptionEmptyTemplate: TemplateRef<any>;

    @ContentChild(OptionTemplateDirective, { read: TemplateRef }) optionTemplate: TemplateRef<any>;

    @ViewChild('filterInput') filterInput?: ElementRef<HTMLInputElement>;
    @ViewChild('origin') origin?: CdkOverlayOrigin;
    @ViewChild(CdkConnectedOverlay) overlay?: CdkConnectedOverlay;

    isOverlayOpen: boolean = false;
    overlayOffsetY: number = 0;
    filterControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });
    filteredItems$: Observable<T[]>;
    selectedItems: R[] = [];

    private innerItemsSubject: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
    private resizeObserver: ResizeObserver | null = null;
    inputValue: string = '';
    onChange: (value: R[]) => void = () => { };
    onTouched: () => void = () => { };

    constructor(private readonly filterByPipe: FilterByPipe) { }

    writeValue(value: R[]): void {
        if (value && value.length !== 0) {
            this.selectedItems = [...value];
            return;
        }
        this.selectedItems = [];
    }

    registerOnChange(fn: (value: R[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    ngOnInit(): void {
        this.setFilteredItems();
    }

    ngAfterViewInit(): void {
        this.observeOriginResize();
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
    }

    private setFilteredItems(): void {
        const filterControlValueChanges$ = this.filterControl.valueChanges.pipe(
            startWith(this.filterControl.value),
            debounceTime(500),
            tap((search: string) => this.inputValue = (search ?? '').trim())
        );

        this.filteredItems$ = combineLatest([
            this.innerItemsSubject.asObservable(),
            filterControlValueChanges$
        ]).pipe(
            map(([items, search]: [T[], string]) => this.filterByPipe.transform(items || [], (search ?? '').trim(), this.bindLabel)
            ));
    }

    toggleSelectAll(filteredItems: T[]): void {
        const selectAll: boolean = !this.selectedItems?.length || (filteredItems.length !== this.selectedItems.length);

        if (selectAll) {
            this.selectedItems = filteredItems.map(item => this.getBindValue(item));
        } else {
            this.selectedItems = [];
        }
        this.onChange(this.selectedItems);
    }

    toggleItemSelection(option: T): void {
        const index = this.selectedItems.findIndex((item: R) => item === this.getBindValue(option));
        if (index === -1) {
            this.selectedItems = [...this.selectedItems, this.getBindValue(option)];
        } else {
            this.selectedItems = [...this.selectedItems.slice(0, index), ...this.selectedItems.slice(index + 1)];
        }
        this.onChange(this.selectedItems);
    }

    removeChip(option: R): void {
        const index = this.selectedItems.findIndex((item: R) => item === option);
        if (index !== -1) {
            this.selectedItems = [...this.selectedItems.slice(0, index), ...this.selectedItems.slice(index + 1)];
            this.onChange(this.selectedItems);
        }
    }

    onChevronClick(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.isOverlayOpen) {
            this.closeOverlay();
            return;
        }

        this.openOverlay();
    }

    openOverlay(): void {
        if (this.isOverlayOpen) {
            return;
        }

        // In chips mode, the origin element height changes as chips wrap; an extra offset can
        // cause the overlay to start too high. Let the overlay attach to the origin bottom.
        this.overlayOffsetY = this.showChipsList ? 0 : this.calculateOverlayOffsetY();
        this.isOverlayOpen = true;
        this.filterControl.setValue('');
        this.inputValue = '';
        if (this.showSearchInput) {
            setTimeout(() => this.filterInput?.nativeElement.focus());
        }
    }

    closeOverlay(): void {
        if (!this.isOverlayOpen) {
            return;
        }

        this.isOverlayOpen = false;
        this.filterControl.setValue('');
        this.inputValue = '';
        this.onTouched();
    }

    onFilterInputKeydown(event: KeyboardEvent): void {
        event.stopPropagation();
        (event as unknown as { stopImmediatePropagation?: () => void }).stopImmediatePropagation?.();

        if (event.key === 'Escape') {
            event.preventDefault();
            this.closeOverlay();
        }

        if (event.key === 'Enter') {
            event.preventDefault();
        }
    }

    onNoDataClick(event: Event): void {
        event.stopPropagation();
        this.closeOverlay();
    }

    onOverlayOutsideClick(event: MouseEvent): void {
        if (!this.showChipsList) {
            return;
        }

        const target = event?.target as HTMLElement | null;
        const originElement = this.origin?.elementRef?.nativeElement;
        if (target && originElement?.contains?.(target)) {
            // Clicking within the origin (chips/input) should not close the overlay.
            return;
        }

        this.closeOverlay();
    }

    private getBindValue(option: T): R {
        const value = typeof option === 'object' && this.bindValue ? option[this.bindValue] : option;
        return value as unknown as R;
    }

    private calculateOverlayOffsetY(): number {
        const originElement = this.origin?.elementRef?.nativeElement;
        if (!originElement) {
            return 0;
        }

        const wrapper = originElement.querySelector('.mat-mdc-text-field-wrapper') as HTMLElement | null;
        if (!wrapper) {
            return 0;
        }

        const originRect = originElement.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        const rawDiff = wrapperRect.bottom - originRect.bottom;
        if (!Number.isFinite(rawDiff)) {
            return 0;
        }

        // Clamp to avoid layout surprises if the DOM changes unexpectedly.
        return Math.max(-100, Math.min(100, Math.round(rawDiff)));
    }

    private observeOriginResize(): void {
        const originElement = this.origin?.elementRef?.nativeElement;
        if (!originElement) {
            return;
        }

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver(() => {
            if (!this.isOverlayOpen) {
                return;
            }

            if (!this.showChipsList) {
                this.overlayOffsetY = this.calculateOverlayOffsetY();
            }

            this.overlay?.overlayRef?.updatePosition();
        });

        this.resizeObserver.observe(originElement);
    }

}
